import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import Header from '../../components/Header';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import { can } from '../../utils/permissions';
import GuideFeeTiersEditor, { getGuideFeeTierDuplicateError } from './GuideFeeTiersEditor';

const saveBtnSx = (colors) => ({
  backgroundColor: colors.blueAccent[700],
  color: colors.grey[100],
  width: 'fit-content',
  fontSize: '14px',
  fontWeight: 'bold',
  padding: '10px 24px',
  textTransform: 'none',
  '&:hover': { backgroundColor: colors.blueAccent[800] },
});

const AddCountryExcursion = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);

  const [details, setDetails] = useState({
    countryId: '',
    departureDate: '',
    excursions: [],
    packages: [],
    pdf: '',
  });

  const [guideFeeTiers, setGuideFeeTiers] = useState([]);
  const guideFeeTierValidationError = getGuideFeeTierDuplicateError(guideFeeTiers);

  const [countryNames, setCountryNames] = useState([]);
  const [excursions, setExcursions] = useState([]);
  /** excursionId -> price override string for this departure only, empty = use the excursion's global price */
  const [priceByExcursion, setPriceByExcursion] = useState({});

  const [packages, setPackages] = useState([]);
  /** packageId -> price override string for this departure only, empty = use the package's global price */
  const [priceByPackage, setPriceByPackage] = useState({});

  const getCountryNames = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/countrynames`, { headers: { token } });
      const data = await res.json();
      setCountryNames(data);
    } catch (error) {
      console.error('Error fetching country names:', error);
    }
  };

  const getExcursions = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/countryExcursions/${details.countryId}`, {
        headers: { token },
      });
      const data = await res.json();
      setExcursions(data.excursions);
    } catch (error) {
      console.error('Error fetching excursions:', error);
    }
  };

  const getPackages = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/countrypackages/${details.countryId}`, {
        headers: { token },
      });
      const data = await res.json();
      const pkgList = data.packages || [];
      setPackages(pkgList);
      const pkgPrices = {};
      pkgList.forEach((p) => {
        pkgPrices[p.PackageID] = '';
      });
      setPriceByPackage(pkgPrices);
      setDetails((prev) => ({ ...prev, packages: [] }));
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  useEffect(() => {
    getCountryNames();
  }, []);

  useEffect(() => {
    if (details.countryId) {
      getExcursions();
      getPackages();
    }
  }, [details.countryId]);

  const handleChange = (e) => {
    setDetails({ ...details, [e.target.name]: e.target.value });
  };

  const handleExcursionToggle = (excursionId) => {
    setDetails((prev) => {
      const exists = prev.excursions.includes(excursionId);
      return {
        ...prev,
        excursions: exists
          ? prev.excursions.filter((id) => id !== excursionId)
          : [...prev.excursions, excursionId],
      };
    });
    setPriceByExcursion((prev) => {
      if (prev[excursionId] === undefined) {
        return { ...prev, [excursionId]: '' };
      }
      const next = { ...prev };
      delete next[excursionId];
      return next;
    });
  };

  const setPriceFor = (excursionId, value) => {
    setPriceByExcursion((prev) => ({ ...prev, [excursionId]: value }));
  };

  const setPriceForPackage = (packageId, value) => {
    setPriceByPackage((prev) => ({ ...prev, [packageId]: value }));
  };

  const handleSelectAll = () => {
    const allIds = excursions.map((e) => e.ExcursionID);
    const allSelected = allIds.every((id) => details.excursions.includes(id));
    setDetails((prev) => ({ ...prev, excursions: allSelected ? [] : allIds }));
  };

  const handlePackageToggle = (packageId) => {
    const isSelecting = !details.packages.includes(packageId);
    const pkgExcursionIds = isSelecting
      ? packages.find((p) => p.PackageID === packageId)?.excursionIds || []
      : [];

    setDetails((prev) => {
      const exists = prev.packages.includes(packageId);
      if (exists) {
        return { ...prev, packages: prev.packages.filter((id) => id !== packageId) };
      }
      // Selecting a package auto-selects all excursions included in it.
      const mergedExcursions = Array.from(new Set([...prev.excursions, ...pkgExcursionIds]));
      return { ...prev, packages: [...prev.packages, packageId], excursions: mergedExcursions };
    });

    if (pkgExcursionIds.length > 0) {
      setPriceByExcursion((prev) => {
        const next = { ...prev };
        pkgExcursionIds.forEach((exId) => {
          if (next[exId] === undefined) next[exId] = '';
        });
        return next;
      });
    }
  };

  const handleSelectAllPackages = () => {
    const allIds = packages.map((p) => p.PackageID);
    const allSelected = allIds.every((id) => details.packages.includes(id));
    setDetails((prev) => ({ ...prev, packages: allSelected ? [] : allIds }));
  };

  const [pdfLoading, setPdfLoading] = useState(false);
  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPdfLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/country-excursion/uploadpdf`, {
        method: 'POST',
        headers: { token },
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setDetails({ ...details, pdf: data.pdf });
        setOpenToast(true);
        setToastmessage('PDF uploaded successfully');
      } else {
        setOpenToast(true);
        setToastmessage(data.message || 'Error uploading PDF');
      }
    } catch (error) {
      console.error('Error uploading PDF:', error);
      setOpenToast(true);
      setToastmessage('Error uploading PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClearPdf = () => {
    setDetails({ ...details, pdf: '' });
  };

  const handlevalidation = () => {
    const { countryId, departureDate, excursions } = details;
    if (!countryId || !departureDate || !excursions || excursions.length === 0) {
      setOpenToast(true);
      setToastmessage('Please fill all Details');
      return false;
    }
    return true;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (handlevalidation()) {
      const dupMsg = getGuideFeeTierDuplicateError(guideFeeTiers);
      if (dupMsg) {
        setOpenToast(true);
        setToastmessage(dupMsg);
        setLoading(false);
        return;
      }
      for (const excursionId of details.excursions) {
        const rawPrice = priceByExcursion[excursionId];
        if (rawPrice !== '' && rawPrice != null && String(rawPrice).trim() !== '') {
          const p = Number(rawPrice);
          if (!Number.isFinite(p) || p < 0) {
            setOpenToast(true);
            setToastmessage('Price override per excursion must be a non-negative number (or leave blank to use the global price).');
            setLoading(false);
            return;
          }
        }
      }
      for (const pkg of packages) {
        const rawPrice = priceByPackage[pkg.PackageID];
        if (rawPrice !== '' && rawPrice != null && String(rawPrice).trim() !== '') {
          const p = Number(rawPrice);
          if (!Number.isFinite(p) || p < 0) {
            setOpenToast(true);
            setToastmessage('Price override per package must be a non-negative number (or leave blank to use the global price).');
            setLoading(false);
            return;
          }
        }
      }
      const excursionsPayload = details.excursions.map((excursionId) => {
        const rawPrice = priceByExcursion[excursionId];
        let price = null;
        if (rawPrice !== '' && rawPrice != null && String(rawPrice).trim() !== '') {
          const globalPrice = excursions.find((e) => e.ExcursionID === excursionId)?.ExcursionPrice;
          const p = Number(rawPrice);
          price = Number.isFinite(p) && p !== Number(globalPrice) ? p : null;
        }
        return { excursionId, maxQuantity: null, price };
      });
      const tierBody = guideFeeTiers.map(({ minEur, maxEur, percentage }) => ({ minEur, maxEur, percentage }));
      const hasAnyTierField = tierBody.some(
        (t) =>
          (t.minEur !== '' && t.minEur != null) ||
          (t.maxEur !== '' && t.maxEur != null) ||
          (t.percentage !== '' && t.percentage != null)
      );
      const packagesPayload = packages
        .filter((pkg) => details.packages.includes(pkg.PackageID))
        .map((pkg) => {
          const rawPrice = priceByPackage[pkg.PackageID];
          let price = null;
          if (rawPrice !== '' && rawPrice != null && String(rawPrice).trim() !== '') {
            const p = Number(rawPrice);
            price = Number.isFinite(p) && p !== Number(pkg.PackagePrice) ? p : null;
          }
          return { packageId: pkg.PackageID, price };
        });

      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/country-excursion`, {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
          token,
        },
        body: JSON.stringify({
          ...details,
          excursions: excursionsPayload,
          tripGuideFees: hasAnyTierField ? { guideFeeTiers: tierBody } : undefined,
          packages: packagesPayload,
        }),
      });
      const data = await res.json();
      if (data.message) {
        setOpenToast(true);
        setToastmessage(data.message);
        if (data.success) {
          setTimeout(() => {
            navigate('/country-excursion');
          }, 2000);
        }
      }
    }
    setLoading(false);
  };

  // Toast
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" rowGap="10px" flexWrap="wrap-reverse">
        <Header title="ADD DESTINATION EXCURSION" subtitle="Add a New Destination Excursion" />

        <Box>
          <Link to={-1}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.grey[100],
                fontSize: '14px',
                fontWeight: 'bold',
                padding: '10px 20px',
                '&:hover': {
                  backgroundColor: colors.blueAccent[800],
                },
              }}
            >
              Go Back
            </Button>
          </Link>
        </Box>
      </Box>

      <form ref={formRef} onSubmit={handleFormSubmit}>
        <Box
          style={{
            display: 'grid',
            gap: '1.25rem',
            paddingBottom: '20px',
          }}
        >
          {/* Country */}
          <TextField
            fullWidth
            variant="filled"
            type="text"
            label="Destination"
            onChange={(e) => {
              setDetails({ ...details, countryId: e.target.value, excursions: [] });
            }}
            value={details.countryId}
            name="countryId"
            select
            required
          >
            {countryNames &&
              countryNames.map((country) => (
                <MenuItem key={country.CountryID} value={country.CountryID}>
                  {country.CountryName}
                </MenuItem>
              ))}
          </TextField>

          {/* Departure Date */}
          <TextField
            type="date"
            fullWidth
            variant="filled"
            label="Departure Date"
            onChange={handleChange}
            value={details.departureDate}
            name="departureDate"
            InputLabelProps={{ shrink: true, style: { color: colors.grey[300] } }}
            style={{ colorScheme: 'dark' }}
            required
          />

          {/* Guide fee tiers (%). If missing, keep/display 0. */}
          <Box
            style={{
              display: 'grid',
              gap: '10px',
              padding: '15px',
              background: colors.primary[400],
              borderRadius: '8px',
            }}
          >
            <Typography variant="h5" fontSize="18px" fontWeight="bold" sx={{ color: colors.grey[100] }}>
              Guide fee ranges (% of paid collections)
            </Typography>
            <GuideFeeTiersEditor value={guideFeeTiers} onChange={setGuideFeeTiers} colors={colors} />
            <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <Button
                type="button"
                variant="contained"
                disabled={loading || Boolean(guideFeeTierValidationError)}
                title={guideFeeTierValidationError || undefined}
                onClick={() => formRef.current?.requestSubmit()}
                sx={saveBtnSx(colors)}
              >
                Save
              </Button>
              <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                Saves destination, guide fee ranges, excursions, and PDF together.
              </Typography>
            </Box>
          </Box>

          {/* Packages */}
          <Box
            sx={{
              display: 'grid',
              gap: '10px',
              padding: '15px',
              background: colors.primary[400],
              borderRadius: '8px',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h5" fontSize="18px" fontWeight="bold" color={colors.grey[100]}>
                Packages
                {details.packages.length > 0 && (
                  <Chip
                    label={`${details.packages.length} selected`}
                    size="small"
                    sx={{ ml: 1.5, backgroundColor: colors.greenAccent[700], color: colors.grey[100], fontWeight: 600 }}
                  />
                )}
              </Typography>
              {packages?.length > 0 && (
                <Button
                  size="small"
                  onClick={handleSelectAllPackages}
                  sx={{ color: colors.blueAccent[300], textTransform: 'none', fontWeight: 600, fontSize: '14px' }}
                >
                  {packages.every((p) => details.packages.includes(p.PackageID)) ? 'Deselect All' : 'Select All'}
                </Button>
              )}
            </Box>
            <Typography variant="caption" sx={{ color: colors.grey[300] }}>
              Only checked packages will be visible/bookable to passengers on this departure date.
              Selecting a package auto-selects the excursions included in it. Set a price override
              only if this departure should charge a different price than the package's global price.
            </Typography>
            {details.countryId ? (
              packages?.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                    gap: '8px',
                    maxHeight: '320px',
                    overflowY: 'auto',
                    pr: 0.5,
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { background: colors.primary[500] },
                    '&::-webkit-scrollbar-thumb': { background: colors.grey[600], borderRadius: '3px' },
                  }}
                >
                  {packages.map((pkg) => {
                    const isChecked = details.packages.includes(pkg.PackageID);
                    return (
                      <Box
                        key={pkg.PackageID}
                        sx={{
                          display: 'grid',
                          gap: '8px',
                          px: 1.5,
                          py: 1,
                          borderRadius: '8px',
                          border: `1.5px solid ${isChecked ? colors.greenAccent[500] : colors.grey[700]}`,
                          backgroundColor: isChecked ? `${colors.greenAccent[900]}44` : colors.primary[500],
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            borderColor: colors.greenAccent[500],
                            backgroundColor: `${colors.greenAccent[900]}22`,
                          },
                        }}
                      >
                        <Box
                          onClick={() => handlePackageToggle(pkg.PackageID)}
                          sx={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                        >
                          {isChecked ? (
                            <CheckBoxIcon sx={{ color: colors.greenAccent[400], fontSize: 20, flexShrink: 0 }} />
                          ) : (
                            <CheckBoxOutlineBlankIcon sx={{ color: colors.grey[500], fontSize: 20, flexShrink: 0 }} />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              color: isChecked ? colors.greenAccent[300] : colors.grey[100],
                              fontWeight: isChecked ? 600 : 400,
                              lineHeight: 1.3,
                            }}
                          >
                            {pkg.PackageName}
                          </Typography>
                        </Box>
                        {isChecked && (
                          <Box display="flex" flexDirection="column" gap="2px" width="100%">
                            {pkg.PackagePrice != null && (
                              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                                Global Price: {pkg.PackagePrice}
                              </Typography>
                            )}
                            <TextField
                              size="small"
                              variant="filled"
                              type="number"
                              label="Price override (blank = global)"
                              disabled={!can('prices.edit')}
                              value={priceByPackage[pkg.PackageID] ?? ''}
                              onChange={(e) => setPriceForPackage(pkg.PackageID, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              inputProps={{ min: 0, step: 0.01 }}
                              fullWidth
                            />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color={colors.grey[400]} sx={{ fontStyle: 'italic', py: 1 }}>
                  No packages found for this destination.
                </Typography>
              )
            ) : (
              <Typography variant="body2" color={colors.grey[400]} sx={{ fontStyle: 'italic', py: 1 }}>
                Please choose a Destination first.
              </Typography>
            )}
          </Box>

          {/* Excursions */}
          <Box
            sx={{
              display: 'grid',
              gap: '10px',
              padding: '15px',
              background: colors.primary[400],
              borderRadius: '8px',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h5" fontSize="18px" fontWeight="bold" color={colors.grey[100]}>
                Excursions
                {details.excursions.length > 0 && (
                  <Chip
                    label={`${details.excursions.length} selected`}
                    size="small"
                    sx={{ ml: 1.5, backgroundColor: colors.greenAccent[700], color: colors.grey[100], fontWeight: 600 }}
                  />
                )}
              </Typography>
              {excursions?.length > 0 && (
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  sx={{ color: colors.blueAccent[300], textTransform: 'none', fontWeight: 600, fontSize: '14px' }}
                >
                  {excursions.every((e) => details.excursions.includes(e.ExcursionID)) ? 'Deselect All' : 'Select All'}
                </Button>
              )}
            </Box>

            {excursions?.length > 0 ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                  gap: '8px',
                  maxHeight: '320px',
                  overflowY: 'auto',
                  pr: 0.5,
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-track': { background: colors.primary[500] },
                  '&::-webkit-scrollbar-thumb': { background: colors.grey[600], borderRadius: '3px' },
                }}
              >
                {excursions.map((excursion) => {
                  const isChecked = details.excursions.includes(excursion.ExcursionID);
                  return (
                    <Box
                      key={excursion.ExcursionID}
                      sx={{
                        display: 'grid',
                        gap: '8px',
                        px: 1.5,
                        py: 1,
                        borderRadius: '8px',
                        border: `1.5px solid ${isChecked ? colors.greenAccent[500] : colors.grey[700]}`,
                        backgroundColor: isChecked ? `${colors.greenAccent[900]}44` : colors.primary[500],
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: colors.greenAccent[500],
                          backgroundColor: `${colors.greenAccent[900]}22`,
                        },
                      }}
                    >
                      <Box
                        onClick={() => handleExcursionToggle(excursion.ExcursionID)}
                        sx={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                      >
                        {isChecked
                          ? <CheckBoxIcon sx={{ color: colors.greenAccent[400], fontSize: 20, flexShrink: 0 }} />
                          : <CheckBoxOutlineBlankIcon sx={{ color: colors.grey[500], fontSize: 20, flexShrink: 0 }} />
                        }
                        <Typography
                          variant="body2"
                          sx={{
                            color: isChecked ? colors.greenAccent[300] : colors.grey[100],
                            fontWeight: isChecked ? 600 : 400,
                            lineHeight: 1.3,
                          }}
                        >
                          {excursion.ExcursionName}
                        </Typography>
                      </Box>
                      {isChecked && (
                        <Box display="flex" flexDirection="column" gap="2px" width="100%">
                          {excursion.ExcursionPrice != null && (
                            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                              Global Price: {excursion.ExcursionPrice}
                            </Typography>
                          )}
                          <TextField
                            size="small"
                            variant="filled"
                            type="number"
                            label="Price override (blank = global)"
                            disabled={!can('prices.edit')}
                            value={priceByExcursion[excursion.ExcursionID] ?? ''}
                            onChange={(e) => setPriceFor(excursion.ExcursionID, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{ min: 0, step: 0.01 }}
                            fullWidth
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color={colors.grey[400]} sx={{ fontStyle: 'italic', py: 1 }}>
                Please choose a Destination first.
              </Typography>
            )}
          </Box>

          {/* PDF Upload */}
          <Box
            style={{
              display: 'grid',
              gap: '10px',
              padding: '15px',
              background: colors.primary[400],
              borderRadius: '8px',
              border: `1px dashed ${colors.grey[500]}`,
            }}
          >
            <Typography variant="h5" fontSize="16px" fontWeight="bold" sx={{ color: colors.grey[100] }}>
              Schedule PDF (Optional)
            </Typography>
            <Box display="flex" alignItems="center" gap="15px" flexWrap="wrap">
              <Button
                variant="contained"
                component="label"
                disabled={pdfLoading}
                startIcon={pdfLoading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: colors.grey[100],
                  '&:hover': { backgroundColor: colors.blueAccent[800] },
                }}
              >
                {details.pdf ? 'Change PDF' : 'Upload PDF'}
                <input type="file" hidden accept="application/pdf" onChange={handlePdfUpload} />
              </Button>

              {details.pdf && (
                <Box display="flex" alignItems="center" gap="10px">
                  <Typography variant="body2" sx={{ color: colors.greenAccent[500], fontWeight: '500', mr: '10px' }}>
                    {details.pdf.split('/').pop() || 'Schedule Uploaded'}
                  </Typography>
                  <a href={details.pdf} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<VisibilityIcon />}
                      sx={{
                        fontWeight: 'bold',
                        color: '#fff',
                        textTransform: 'uppercase',
                        '& .MuiButton-startIcon': { mr: '5px' },
                      }}
                    >
                      View
                    </Button>
                  </a>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: colors.redAccent[600],
                      fontWeight: 'bold',
                      color: '#fff',
                      textTransform: 'uppercase',
                      '&:hover': { backgroundColor: colors.redAccent[700] },
                      '& .MuiButton-startIcon': { mr: '5px' },
                    }}
                    startIcon={<DeleteIcon />}
                    onClick={handleClearPdf}
                  >
                    Clear
                  </Button>
                </Box>
              )}
            </Box>
            {pdfLoading && (
              <Typography variant="caption" sx={{ color: colors.blueAccent[200] }}>
                Uploading schedule...
              </Typography>
            )}
          </Box>

          <Button
            type="submit"
            disabled={loading || Boolean(guideFeeTierValidationError)}
            title={guideFeeTierValidationError || undefined}
            sx={{ ...saveBtnSx(colors), padding: '10px 30px' }}
          >
            {loading ? 'Adding...' : 'Add'}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default AddCountryExcursion;
